import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { inr, inrCompact } from '../poPayment';

const PALETTE = ['#c026d3', '#0ea5e9', '#10b981', '#f59e0b', '#6366f1', '#94a3b8'];
const monthLabel = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

function buildSeries(trend, split) {
  if (split === 'total') return { keys: ['total'], data: trend.map((t) => ({ month: t.month, label: monthLabel(t.month), count: t.count, total: t.total })) };
  const field = split === 'client' ? 'by_client' : 'by_category';
  const totals = {};
  trend.forEach((t) => Object.entries(t[field]).forEach(([k, v]) => { totals[k] = (totals[k] || 0) + v; }));
  const top = Object.entries(totals).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k]) => k);
  const hasOther = Object.keys(totals).length > top.length;
  const data = trend.map((t) => {
    const row = { month: t.month, label: monthLabel(t.month), count: t.count };
    let other = 0;
    Object.entries(t[field]).forEach(([k, v]) => { if (top.includes(k)) row[k] = v; else other += v; });
    if (hasOther) row.Other = other;
    return row;
  });
  return { keys: hasOther ? [...top, 'Other'] : top, data };
}

const TipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const sum = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-slate-900/90 text-white rounded-lg px-3 py-2 text-xs shadow-xl backdrop-blur-sm border border-slate-700">
      <p className="font-semibold mb-1">{label} · {inr(sum)}</p>
      {payload.length > 1 && payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.dataKey}: {inr(p.value)}</p>
      ))}
    </div>
  );
};

export default function POTrendChart({ trend, onDrill }) {
  const [split, setSplit] = React.useState('total');
  const { keys, data } = React.useMemo(() => buildSeries(trend || [], split), [trend, split]);
  return (
    <div className="soft-card p-4 sm:p-5" data-testid="po-analytics-trend-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h3 className="font-semibold text-[#1a1a2e]">Spend over time</h3>
          <p className="text-xs text-slate-500">Monthly PO value · click a bar to see that month&apos;s POs</p>
        </div>
        <div className="flex p-0.5 bg-slate-100 rounded-lg">
          {[['total', 'Total'], ['client', 'By client'], ['category', 'By category']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setSplit(k)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${split === k ? 'bg-white text-fuchsia-700 shadow-sm' : 'text-slate-600'}`}
              data-testid={`po-trend-split-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      {data.length === 0 ? (
        <div className="h-[300px] flex items-center justify-center text-sm text-slate-500">No dated POs in this range</div>
      ) : (
        <div className="h-[300px] [&_.recharts-wrapper]:outline-none [&_.recharts-surface]:outline-none [&_*:focus]:outline-none">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              onClick={(e) => {
                // Recharts v3 no longer passes activePayload; resolve the row via activeIndex.
                const row = e?.activePayload?.[0]?.payload || (e?.activeIndex != null ? data[Number(e.activeIndex)] : null);
                if (row && row.count > 0) onDrill({ month: row.month, title: row.label });
              }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={inrCompact} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={56} />
              <Tooltip content={<TipBox />} cursor={{ fill: 'rgba(192,38,211,0.06)' }} />
              {keys.length > 1 && <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />}
              {keys.map((k, i) => (
                <Bar key={k} dataKey={k} stackId="a" fill={split === 'total' ? PALETTE[0] : PALETTE[i % PALETTE.length]}
                  radius={i === keys.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]} maxBarSize={48} className="cursor-pointer" />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
